import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import React, { useCallback } from 'react'
import styled from 'styled-components'
import ExperienceSearchBar from './ExperienceSearchBar/ExperienceSearchBar'
import ExperienceSearchPageSort from './ExperienceSearchPageSort'
import TextButton from 'components/Luxkit/Button/TextButton'
import ExperiencesSearchForm from './ExperienceSearchForm'
import Pane from 'components/Common/Pane'
import LineListUlIcon from 'components/Luxkit/Icons/line/LineListUlIcon'
import LineMapIcon from 'components/Luxkit/Icons/line/LineMapIcon'
import LineFilterAltIcon from 'components/Luxkit/Icons/line/LineFilterAltIcon'
import AssistChip from 'components/Luxkit/Chips/AssistChip'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'

const Wrapper = styled(Pane)`
  display: grid;
  padding-bottom: ${rem(16)};
  padding-top: ${rem(16)};
  gap: ${rem(16)};
`

const StyledExperienceSearchBar = styled(ExperienceSearchBar)`
  ${mediaQueryUp.tablet} {
    display: none;
  }

  &.edit-mode {
    display: none;
  }
`

const SearchFormContainer = styled(LayoutContainer)`
  display: none;

  ${mediaQueryUp.tablet} {
    display: block;
  }
`

const FilterContainer = styled(LayoutContainer)`
  display: flex;
  gap: ${rem(8)};

  ${mediaQueryUp.tablet} {
    justify-content: flex-end;
  }
`

const StyledButton = styled(TextButton)`
  flex: 1;

  ${mediaQueryUp.tablet} {
    flex: unset
  }
`

const SortAndFilterButton = styled(StyledButton)`
  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const DesktopFilter = styled(AssistChip)`
  margin-right: auto;

  ${mediaQueryDown.tablet} {
    display: none;
  }
`

interface Props {
  list: App.ApiCallState<{
    ids: string[];
    placeId: string;
  }>
  toggleShowFilter: () => void
  searchMode: Experiences.SearchMode
}

function ExperienceSearchHeader(props: Props) {
  const { toggleShowFilter, list, searchMode } = props
  const trackEvent = useTrackExperienceOfferEvent()

  const handleViewMapClick = useCallback(() => {
    trackEvent('VIEW_ON_MAP_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [])

  const handleViewAllClick = useCallback(() => {
    trackEvent('VIEW_ALL_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [])

  return <Wrapper>
    <StyledExperienceSearchBar />
    <SearchFormContainer>
      <ExperiencesSearchForm />
    </SearchFormContainer>
    <FilterContainer>
      {searchMode.mode === 'map' &&
        <DesktopFilter size="medium" variant="filled" startIcon={<LineFilterAltIcon />} onClick={toggleShowFilter}>
          Filter offers
        </DesktopFilter>
      }
      {searchMode.mode === 'list' &&
        <CSSBreakpoint min="desktop" as="span">
          <ExperienceSearchPageSort />
        </CSSBreakpoint>
      }
      {list?.ids.length > 0 &&
        <SortAndFilterButton
          kind="secondary"
          variant="dark"
          startIcon={<LineFilterAltIcon />}
          onClick={toggleShowFilter}
          data-testid="sort-and-filter-button"
        >
          {searchMode.mode === 'list' && 'Sort & '}Filter
        </SortAndFilterButton>
      }

      {searchMode.mode === 'list' && <StyledButton
        kind="secondary"
        variant="dark"
        to={searchMode.url}
        startIcon={<LineMapIcon />}
        onClick={handleViewMapClick}
        data-testid="map-view-button"
        fit="mobile-full-width"
      >
        Map view
      </StyledButton>}
      {searchMode.mode === 'map' && <StyledButton
        kind="secondary"
        variant="dark"
        to={searchMode.url}
        startIcon={<LineListUlIcon />}
        onClick={handleViewAllClick}
        data-testid="list-view-button"
        fit="mobile-full-width"
      >
        List view
      </StyledButton>}

    </FilterContainer>
  </Wrapper>
}

export default ExperienceSearchHeader
